import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, MapPin, Clock } from "lucide-react";
import api from "../lib/api";
import { formatKobo } from "../lib/format";
import { useSettings } from "../context/SettingsContext";

export default function Confirmation() {
  const { reference } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { settings } = useSettings();

  useEffect(() => {
    api.get(`/orders/ref/${reference}`).then(({ data }) => setOrder(data)).catch(() => setNotFound(true));
  }, [reference]);

  if (notFound) return <div className="max-w-xl mx-auto px-4 py-24 text-center text-sapore-inkSoft">Order not found.</div>;
  if (!order) return <div className="max-w-xl mx-auto px-4 py-24 text-center text-sapore-muted">Loading…</div>;

  const number = settings?.whatsapp || "2348094706281";
  const itemLines = order.items
    .map((it) => `${it.quantity}x ${it.name}${it.variant ? ` (${it.variant})` : ""} — ${formatKobo(it.line_total)}`)
    .join("\n");
  const message = `Hello Sapore Eats! I've just placed and paid for an order.

Order Ref: ${order.reference}
Name: ${order.customer.name}
Phone: ${order.customer.phone}

ITEMS
${itemLines}

ALLERGIES: ${order.allergy_flag ? [order.allergy_tags?.join(", "), (order.allergy_note || "").trim()].filter(Boolean).join(" — ") : "None noted"}

Fulfilment: Delivery — ${order.zone_name}
Address: ${order.customer.address}
Delivery fee: ${formatKobo(order.delivery_fee)}
TOTAL PAID: ${formatKobo(order.total)}
${order.notes ? `\nNotes: ${order.notes}` : ""}

Please confirm my estimated delivery time. Thank you!`;
  const waLink = `https://wa.me/${number}?text=${encodeURIComponent(message)}`;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-sapore-whatsapp/15 text-sapore-whatsapp flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 size={36} />
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-sapore-purple">Order confirmed!</h1>
        <p className="text-sapore-inkSoft mt-2">Payment received. Your reference is</p>
        <p data-testid="order-reference" className="font-serif text-2xl font-bold text-sapore-gold mt-1">{order.reference}</p>
      </div>

      <a
        data-testid="whatsapp-order-button"
        href={waLink}
        target="_blank"
        rel="noreferrer"
        className="mt-8 w-full flex items-center justify-center gap-3 bg-sapore-whatsapp hover:bg-sapore-whatsappDark text-white font-semibold py-4 rounded-full text-lg transition-colors hover:scale-[1.01] active:scale-[0.99]"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24z"/></svg>
        Send order to WhatsApp
      </a>
      <p className="text-center text-xs text-sapore-muted mt-2">We'll confirm your estimated delivery time by WhatsApp shortly.</p>

      <div className="mt-8 bg-white rounded-2xl border border-sapore-border p-6">
        <h2 className="font-serif text-xl text-sapore-ink mb-4">Order summary</h2>
        <ul className="space-y-3">
          {order.items.map((it, i) => (
            <li key={`${it.item_id || it.name}-${i}`} className="flex justify-between text-sm">
              <span className="text-sapore-inkSoft">
                {it.quantity}× {it.name}{it.variant ? ` (${it.variant})` : ""}
                {it.addons?.length > 0 && <span className="block text-xs text-sapore-muted">+ {it.addons.map((a) => a.name).join(", ")}</span>}
              </span>
              <span className="font-medium">{formatKobo(it.line_total)}</span>
            </li>
          ))}
        </ul>
        <div className="border-t border-sapore-border mt-4 pt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-sapore-inkSoft">Subtotal</span><span>{formatKobo(order.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-sapore-inkSoft">Delivery ({order.zone_name})</span><span>{formatKobo(order.delivery_fee)}</span></div>
          <div className="flex justify-between font-bold text-lg pt-2"><span>Total paid</span><span className="text-sapore-gold">{formatKobo(order.total)}</span></div>
        </div>
        <div className="mt-5 flex items-start gap-2 text-sm text-sapore-inkSoft">
          <MapPin size={16} className="mt-0.5 text-sapore-purple" /> {order.customer.address}
        </div>
        {order.allergy_flag ? (
          <div className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3" data-testid="confirmation-allergy">
            <p className="text-sm font-semibold text-red-700">⚠ Allergies noted: {(order.allergy_tags || []).join(", ")}</p>
            {order.allergy_note && <p className="text-sm text-red-700/90 mt-0.5">{order.allergy_note}</p>}
          </div>
        ) : (
          <p className="mt-3 text-sm text-sapore-muted" data-testid="confirmation-allergy">No allergies noted.</p>
        )}
        <div className="mt-2 flex items-start gap-2 text-sm text-sapore-inkSoft">
          <Clock size={16} className="mt-0.5 text-sapore-purple" /> {order.eta || "Estimated time to be confirmed"}
        </div>
      </div>

      <div className="text-center mt-8">
        <Link to="/menu" className="text-sapore-purple font-medium hover:text-sapore-gold">Order again</Link>
      </div>
    </div>
  );
}
